/**
 * Erfahrung und Level des Heroes (Masterprompt Abschnitt 45 bis 47).
 *
 * Hero-XP ist bewusst von der Wirtschaft getrennt: Man kauft sie nicht mit
 * Gold, man erkaempft sie. Alle Zahlen hier sind TUNING_PLACEHOLDER.
 */

/**
 * Erfahrung je Creep, gestaffelt nach Tier (Produktentscheidung vom
 * 23.09.2026).
 *
 * Bewusst NICHT an den Goldpreis gekoppelt: Ein Trader kostet so viel wie ein
 * Archer, kaempft aber nicht - er soll auch nicht so viel Erfahrung bringen.
 * Der Index ist das Tier minus eins.
 */
export const XP_PER_TIER: readonly number[] = [8, 19, 42, 95];
// Seit dem 29.09.2026 rund das Doppelte der frueheren 4/9/20/45
// (Produktentscheidung). Davor endete JEDER Hero auf Level 7 oder 8 - Level 10
// und damit der Capstone wurden in 0 Prozent der Matches erreicht. Der Faktor
// ist ausgemessen: Bei 1,8 erreichten 27 Prozent Level 10, bei 2,0 waren es
// 41, bei 2,2 schon 62. Mit 2,1 sind es 50.

/**
 * Aufschlag fuer Bosse (Tag "boss"), als Anteil.
 *
 * Produktentscheidung vom 23.09.2026: Ein Boss soll sich lohnen, aber nicht
 * mehrere Level auf einmal bringen.
 */
export const BOSS_XP_BONUS = 0.2;


/**
 * Faktor auf den Erfahrungsbedarf eines Heroes - derzeit LEER
 * (Produktentscheidung vom 09.10.2026).
 *
 * Wer nicht eingetragen ist, braucht die normale Menge. Eingetragen ist
 * niemand: Alle vier Heroes steigen auf derselben Leiter auf.
 *
 * Hier stand bis zum 09.10.2026 "vanguard: 0.95" (Produktentscheidung vom
 * 29.09.2026). Der Grund war eine andere Regel: Kills, die jemand anderes
 * fuer den Hero erledigte, zaehlten nur zur Haelfte, und der Vanguard liess
 * 34 bis 41 Prozent seiner Arbeit von Defendern machen - gemessen ueber 720
 * Matches endete er auf Level 8,9 und erreichte seinen Capstone in 0 Prozent
 * der Matches, waehrend der Deadeye ihn zu 100 Prozent erreichte.
 *
 * Diese Halbierung gibt es nicht mehr (Abschnitt 5c): Erfahrung haengt an
 * der Praesenz an der Front, nicht daran, wer zugeschlagen hat. Damit lief
 * die Verguenstigung ohne Rechtfertigung weiter - ausgerechnet beim
 * staerksten Helden (61 Prozent Siegquote gegen 40 beim Shade).
 *
 * Der Mechanismus bleibt: Eine kuerzere Leiter ist ein brauchbares Mittel,
 * wenn ein Hero strukturell langsamer an Erfahrung kommt. Nur braucht ihn
 * gerade keiner. TUNING_PLACEHOLDER.
 */
export const HERO_XP_FACTOR: Readonly<Record<string, number>> = {};

/** Faktor auf den Erfahrungsbedarf. 1, wenn der Hero keinen eigenen hat. */
export function getHeroXpFactor(heroId: string): number {
  return HERO_XP_FACTOR[heroId] ?? 1;
}

/**
 * Gesamte Erfahrung, die fuer jedes Level noetig ist.
 *
 * Der Index ist das Level minus eins: Level 1 ab 0 XP, Level 2 ab 80 XP.
 *
 * Vorne gestreckt (Produktentscheidung vom 05.10.2026). Gemessen ueber 288
 * Spieler-Eintraege war der Anfang viel zu dicht: Level 2 nach 20 Sekunden,
 * Level 5 nach 2:16 - in den ersten vier Minuten stieg man SECHSMAL auf,
 * also alle 40 bis 70 Sekunden. Genau in der Phase, in der ohnehin alles
 * gleichzeitig passiert.
 *
 * Ziel ist jetzt rund ein Level je Minute bis Level 8, danach langsamer.
 * Level 10 bleibt, wo es war - es lag mit 11:07 gut.
 *
 *   Level     2     3     4     5     6     7     8     9    10
 *   vorher 0:20  0:42  1:21  2:16  3:26  4:35  6:12  8:26 11:07
 *   Ziel   1:00  2:00  3:00  4:00  5:00  6:00  7:00  9:00 11:00
 *
 * Die Zahlen sind nicht geschaetzt, sondern aus der gemessenen XP-Kurve
 * zurueckgerechnet: Zu jedem Aufstiegszeitpunkt ist bekannt, wie viel XP ein
 * Spieler hatte - dazwischen wurde linear interpoliert.
 *
 * Leicht nachgerundet, damit die Abstaende durchgehend WACHSEN. Roh ergab
 * die Rechnung 85 fuer Level 2 und 160 fuer Level 3, also einen kleineren
 * zweiten Schritt als den ersten - eine Leiter, die kurz zurueckliefe.
 *
 * Level 11 und hoeher bleiben unberuehrt (XP_PER_LEVEL_ABOVE_10): Sie sind
 * Fueller fuer lange Matches und erreichen ohnehin nur 35 Prozent.
 */
export const LEVEL_XP_THRESHOLDS: readonly number[] = [
  0, 80, 165, 260, 380, 510, 640, 775, 1065, 1365,
];

/** Erfahrung je weiterem Level ueber Level 10 hinaus. */
export const XP_PER_LEVEL_ABOVE_10 = 450;

/** Hoechstes erreichbares Level. TUNING_PLACEHOLDER. */
export const MAX_HERO_LEVEL = 15;

/**
 * Zuwachs der Grundwerte je Level, als Anteil des Startwerts.
 *
 * Bewusst linear auf den Startwert gerechnet und nicht multiplikativ: So bleibt
 * der Zuwachs ueber alle Level gleich gross und ist leicht nachzurechnen.
 */
export const LEVEL_STAT_GROWTH = {
  maxHp: 0.05,
  maxMana: 0.05,
  attackDamage: 0.04,
} as const;

/** Ab diesem Level steht das Ultimate zur Verfuegung (Abschnitt 46). */
export const ULTIMATE_UNLOCK_LEVEL = 5;

/** Level, die einen freien Skillpunkt geben (Abschnitt 46). */
export const SKILL_POINT_LEVELS: readonly number[] = [2, 4, 6];

/** Level, auf dem ein Talent gewaehlt wird. */
export const TALENT_LEVEL = 8;

/** Level, auf dem der Capstone gewaehlt wird. */
export const CAPSTONE_LEVEL = 10;
